import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View, ViewStyle } from 'react-native';

import { DuelHudInfoSvg, DuelHudMorePng } from '../../../assets/duels';
import NotificationBellPng from '../../../assets/notification-bell.png';
import { IconSoundsSvg } from '../../../assets/settings';
import { GradientFill } from '../common/GradientFill';
import { TouchableOpacity } from '../common/Touchable';
import {
	DUEL_HUD_FONT,
	DUEL_HUD_HISTORY_FILL,
	DUEL_HUD_SHELL_FILL,
	duelHudColors,
} from './duelHudTheme';

export type DuelMoreMenuItem = 'notifications' | 'sound' | 'settings';

export const duelMoreMenuSize = (plate: number, gap: number, items: number, open: boolean) => {
	const width = plate + 2 * gap;
	return { width, height: open ? width + items * (plate + gap) : width };
};

type Props = {
	open: boolean;
	onToggle: () => void;
	plate: number;
	gap: number;
	shellWhenClosed: boolean;
	items: DuelMoreMenuItem[];
	soundOn: boolean;
	unreadCount: number;
	onSound: () => void;
	onGameSettings: () => void;
	onNotifications: () => void;
	style: ViewStyle;
};

const UNREAD_CAP = 99;

const Badge = ({ count, size }: { count: number; size: number }) => (
	<View
		pointerEvents='none'
		style={[
			styles.badge,
			{
				minWidth: size,
				height: size,
				borderRadius: size / 2,
				paddingHorizontal: size * 0.25,
				top: -size * 0.3,
				right: -size * 0.3,
			},
		]}
	>
		<Text style={[styles.badgeText, { fontSize: size * 0.62 }]}>
			{count > UNREAD_CAP ? `${UNREAD_CAP}+` : count}
		</Text>
	</View>
);

const Dots = ({ open, size }: { open: boolean; size: number }) => (
	<Image
		source={DuelHudMorePng}
		resizeMode='contain'
		style={{
			width: size * 0.6,
			height: size * 0.6,
			transform: [{ rotate: open ? '90deg' : '0deg' }],
		}}
	/>
);

export const DuelMoreMenu = ({
	open,
	onToggle,
	plate,
	gap,
	shellWhenClosed,
	items,
	soundOn,
	unreadCount,
	onSound,
	onGameSettings,
	onNotifications,
	style,
}: Props) => {
	const { t } = useTranslation();
	const { width, height } = duelMoreMenuSize(plate, gap, items.length, open);
	const plateRadius = plate * 0.28;
	const shellRadius = width * 0.3;
	const icon = plate * 0.56;
	const badgeSize = plate * 0.46;
	const showShell = open || shellWhenClosed;
	const notificationsInMenu = items.includes('notifications');

	const plateStyle = (highlighted: boolean) => [
		styles.centered,
		styles.plate,
		{
			width: plate,
			height: plate,
			borderRadius: plateRadius,
			borderWidth: Math.max(1, plate / 36),
			borderColor: highlighted ? duelHudColors.accent : duelHudColors.historyBorder,
		},
	];

	const renderItem = (item: DuelMoreMenuItem) => {
		if (item === 'sound') {
			return (
				<TouchableOpacity
					key={item}
					sound={false}
					onPress={onSound}
					accessibilityRole='switch'
					accessibilityLabel={t('T_SITE_SETTINGS_SOUND', { defaultValue: 'Sound effects' })}
					aria-checked={soundOn}
					style={plateStyle(false)}
				>
					<GradientFill stops={DUEL_HUD_HISTORY_FILL} radius={plateRadius} />
					<View pointerEvents='none' style={{ opacity: soundOn ? 1 : 0.55 }}>
						<IconSoundsSvg width={icon} height={icon} />
					</View>
					{soundOn ? null : (
						<View
							pointerEvents='none'
							style={[
								styles.mute,
								{ width: icon * 1.15, height: Math.max(2, plate / 18), top: (plate - 2) / 2 - 1 },
							]}
						/>
					)}
				</TouchableOpacity>
			);
		}
		if (item === 'settings') {
			return (
				<TouchableOpacity
					key={item}
					onPress={onGameSettings}
					accessibilityRole='button'
					accessibilityLabel={t('T_SITE_MENU_SETTINGS', { defaultValue: 'Settings' })}
					style={plateStyle(false)}
				>
					<GradientFill stops={DUEL_HUD_HISTORY_FILL} radius={plateRadius} />
					<View pointerEvents='none'>
						<DuelHudInfoSvg width={icon} height={icon} />
					</View>
				</TouchableOpacity>
			);
		}
		return (
			<TouchableOpacity
				key={item}
				onPress={onNotifications}
				accessibilityRole='button'
				accessibilityLabel={`${t('T_SITE_SETTINGS_NOTIFICATIONS', { defaultValue: 'Notifications' })}${
					unreadCount > 0 ? ` (${unreadCount})` : ''
				}`}
				style={plateStyle(false)}
			>
				<GradientFill stops={DUEL_HUD_HISTORY_FILL} radius={plateRadius} />
				<Image
					source={NotificationBellPng}
					resizeMode='contain'
					style={{ width: icon, height: icon }}
				/>
				{unreadCount > 0 ? <Badge count={unreadCount} size={badgeSize} /> : null}
			</TouchableOpacity>
		);
	};

	return (
		<View pointerEvents='box-none' style={[style, { width, height }]}>
			{showShell ? (
				<View
					pointerEvents='none'
					style={[
						StyleSheet.absoluteFill,
						styles.shell,
						{ borderRadius: shellRadius, borderWidth: Math.max(1, plate / 30) },
					]}
				>
					<GradientFill stops={DUEL_HUD_SHELL_FILL} radius={shellRadius} />
				</View>
			) : null}
			<View style={[styles.column, { padding: gap, rowGap: gap }]}>
				<TouchableOpacity
					onPress={onToggle}
					accessibilityRole='button'
					accessibilityLabel={t('T_SITE_MENU_SETTINGS', { defaultValue: 'Settings' })}
					aria-expanded={open}
					style={plateStyle(open)}
				>
					<GradientFill stops={DUEL_HUD_HISTORY_FILL} radius={plateRadius} />
					<Dots open={open} size={plate} />
					{!open && notificationsInMenu && unreadCount > 0 ? (
						<Badge count={unreadCount} size={badgeSize} />
					) : null}
				</TouchableOpacity>
				{open ? items.map(renderItem) : null}
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	row: { flexDirection: 'row' },
	column: { flexDirection: 'column' },
	centered: { alignItems: 'center', justifyContent: 'center' },
	plate: { overflow: 'visible' },
	shell: {
		borderColor: duelHudColors.stroke,
		overflow: 'hidden',
		shadowColor: 'rgba(0,168,255,0.45)',
		shadowOpacity: 1,
		shadowRadius: 10,
		shadowOffset: { width: 0, height: 0 },
	},
	mute: {
		position: 'absolute',
		backgroundColor: '#FF5A4A',
		borderRadius: 2,
		transform: [{ rotate: '-45deg' }],
	},
	badge: {
		position: 'absolute',
		backgroundColor: '#E5322D',
		alignItems: 'center',
		justifyContent: 'center',
		borderWidth: 1,
		borderColor: '#FFFFFF',
	},
	badgeText: { color: '#FFFFFF', fontFamily: DUEL_HUD_FONT, fontWeight: '800' },
});
